import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { MapPin, ArrowUpRight, Award, Sparkles, ImagePlus } from "lucide-react";
import PageHead from "@/components/PageHead";
import BeforeAfterStack from "@/components/BeforeAfterStack";
import { useT } from "@/context/ContentContext";

const CATEGORIES = ["todos", "Remodelações completas", "Cozinhas", "Casas de Banho", "Pinturas", "Pavimentos", "Outros trabalhos"];

// Fallback for the top before/after slider stack — replaced by CMS Antes/Depois when present
const BA_FALLBACK = [
    {
        title: "Cozinha renovada",
        subtitle: "Apartamento T2 · Oeiras",
        before: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        after: "https://images.unsplash.com/photo-1714860534425-7ce04e013dec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
    {
        title: "Casa de banho premium",
        subtitle: "Moradia · Cascais",
        before: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        after: "https://images.unsplash.com/photo-1763485955998-a9284d042d50?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
    {
        title: "Sala e acabamentos",
        subtitle: "Apartamento T3 · Lisboa",
        before: "https://images.unsplash.com/photo-1554995207-c18c203602cb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
        after: "https://images.unsplash.com/photo-1738168279272-c08d6dd22002?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
];

export default function Portfolio() {
    const t = useT();
    const [projects, setProjects] = useState([]);
    const [ba, setBa] = useState([]);
    const [filter, setFilter] = useState("todos");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/projects").then(r => r.data || []).catch(() => []),
            api.get("/before-after").then(r => r.data || []).catch(() => []),
        ]).then(([projs, baItems]) => {
            setProjects(projs);
            setBa(baItems.length ? baItems.map(it => ({
                title: it.title,
                subtitle: it.subtitle,
                before: fileUrl(it.before_id),
                after: fileUrl(it.after_id),
            })) : BA_FALLBACK);
            setLoading(false);
        });
    }, []);

    const filtered = filter === "todos" ? projects : projects.filter((p) => p.category === filter);

    return (
        <>
            <PageHead
                title="Obras & Transformações — O Homem da Obra"
                description="Portefólio de obras realizadas e transformações antes/depois. Uma amostra do rigor, coordenação e cuidado nos acabamentos em cada intervenção."
                canonical="https://ohomemdaobra.pt/trabalhos"
            />

            {/* HEADER */}
            <section className="pt-14 pb-8 sm:pt-20 sm:pb-10">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
                    <div className="eyebrow mb-6"><Award className="w-3.5 h-3.5" /> {t("trabalhos.eyebrow", "Obras & Transformações")}</div>
                    <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold text-stone-900 tracking-tight leading-[1.02] max-w-4xl" data-testid="obras-h1">
                        {t("trabalhos.title_prefix", "Portefólio de")} <span className="font-serif-italic text-[hsl(var(--moss))]">{t("trabalhos.title_italic", "obras realizadas")}</span>.
                    </h1>
                    <p className="text-stone-600 text-lg mt-8 max-w-2xl">
                        {t("trabalhos.subtitle", "Comece por arrastar as transformações abaixo — depois explore o portefólio completo.")}
                    </p>
                </div>
            </section>

            {/* 3 STACKED BEFORE/AFTER SLIDERS */}
            <section className="pb-16 sm:pb-24">
                <div className="max-w-5xl mx-auto px-6 sm:px-10">
                    <div className="eyebrow mb-6"><Sparkles className="w-3.5 h-3.5" /> {t("trabalhos.ba_eyebrow", "Antes & Depois")}</div>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.1] mb-10">
                        {t("trabalhos.ba_title", "Arraste sobre a imagem para comparar")}<span className="text-[hsl(var(--copper))]">.</span>
                    </h2>
                    <BeforeAfterStack items={ba.slice(0, 3)} />
                </div>
            </section>

            {/* SEPARATOR + PORTFOLIO GRID — only when there's at least one published project */}
            {projects.length > 0 && (
                <>
                    <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
                        <div className="h-px bg-[hsl(var(--graphite))]/10" />
                    </div>

                    <section className="py-14 sm:py-20">
                        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
                            <div className="eyebrow mb-6"><Award className="w-3.5 h-3.5" /> {t("trabalhos.grid_eyebrow", "Trabalhos")}</div>
                            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.1] max-w-3xl mb-6">
                                {t("trabalhos.grid_title", "Obras realizadas por categoria")}<span className="text-[hsl(var(--copper))]">.</span>
                            </h2>

                            {/* FILTERS */}
                            <div className="flex flex-wrap gap-2 mt-10" data-testid="portfolio-filters">
                                {CATEGORIES.map((c) => (
                                    <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c}`}
                                        className={`px-4 py-2 rounded-full text-sm border transition ${filter === c
                                            ? "bg-[hsl(var(--forest))] text-white border-[hsl(var(--forest))]"
                                            : "bg-transparent text-[hsl(var(--graphite))] border-[hsl(var(--graphite))]/20 hover:border-[hsl(var(--forest))]"}`}>
                                        {c === "todos" ? "Todos" : c}
                                    </button>
                                ))}
                            </div>

                            {/* GRID */}
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
                                {filtered.map((p) => (
                                    <Link key={p.id} to={`/trabalhos/${p.slug}`} className="card-stone card-stone-hover p-4 group" data-testid={`project-card-${p.slug}`}>
                                        <div className="rounded-2xl overflow-hidden aspect-[4/3] mb-4 bg-stone-100">
                                            <img src={/^https?:\/\//.test(p.cover_file_id || "") ? p.cover_file_id : (p.cover_file_id ? fileUrl(p.cover_file_id) : p.cover)} alt={p.title} loading="lazy"
                                                className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--copper))] mb-1">{p.category}</div>
                                        <h3 className="font-display text-lg font-bold mb-1">{p.title}</h3>
                                        {p.location && <div className="flex items-center gap-1 text-xs text-[hsl(var(--graphite))]/60"><MapPin className="w-3 h-3" /> {p.location}</div>}
                                    </Link>
                                ))}
                            </div>

                            {filtered.length === 0 && (
                                <div className="text-center py-16 border-2 border-dashed border-stone-300 rounded-2xl mt-6" data-testid="portfolio-empty">
                                    <ImagePlus className="w-8 h-8 text-stone-400 mx-auto mb-3" />
                                    <p className="text-stone-500 mb-1">Nenhuma obra nesta categoria.</p>
                                </div>
                            )}
                        </div>
                    </section>
                </>
            )}

            {/* CTA */}
            <section className="pb-20">
                <div className="max-w-[1400px] mx-auto px-3 sm:px-4">
                    <div className="bg-[hsl(var(--forest))] rounded-[2rem] p-10 sm:p-16 text-center text-white">
                        <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight max-w-2xl mx-auto">
                            {t("trabalhos.cta_title", "Quer transformar o seu imóvel?")}
                        </h2>
                        <Link to="/orcamento" className="btn-copper mt-8">
                            Fale-nos da sua obra
                            <span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span>
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
